import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart2, Users, Layers, CreditCard, TrendingUp, Settings,
  ChevronRight, LogOut, GraduationCap, ScrollText, Shield, BookOpen, UserCog, Award, CheckCircle2,
  Menu, X, KeyRound, Sparkles, LayoutDashboard, Megaphone,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ThemeToggle } from "../context/ThemeContext";
import { api } from "../lib/api";
import { canAccess, canAccessAny } from "../lib/auth-tokens";
import { clearAdminDeviceToken } from "../lib/device";
import { AdminProfileModal } from "../components/admin/AdminProfileModal";
import { AdminErrorBoundary } from "../components/admin/AdminErrorBoundary";
import { Backdrop } from "../components/ui/Backdrop";

type NavItem = { to: string; label: string; icon: typeof BarChart2; end?: boolean; feature?: string; features?: string[] };
type NavSection = { title: string; items: NavItem[] };

const navSections: NavSection[] = [
  {
    title: "Dashboard",
    items: [
      { to: "/admin", label: "Overview", icon: BarChart2, end: true, feature: "overview" },
      { to: "/admin/analytics", label: "Analytics", icon: TrendingUp, feature: "analytics.read" },
    ],
  },
  {
    title: "Management",
    items: [
      { to: "/admin/users", label: "Users", icon: Users, feature: "users.read" },
      { to: "/admin/courses", label: "Courses", icon: BookOpen, feature: "resources.read" },
      { to: "/admin/resources", label: "Resources", icon: Layers, feature: "resources.read" },
      { to: "/admin/coc", label: "COC exams", icon: Award, feature: "resources.read" },
      { to: "/admin/content-review", label: "Content review", icon: CheckCircle2, feature: "resources.write" },
      { to: "/admin/payments", label: "Payments", icon: CreditCard, feature: "payments.read" },
      { to: "/admin/packages", label: "Premium packages", icon: Sparkles, feature: "payments.read" },
      { to: "/admin/catalog", label: "Catalog", icon: BookOpen, features: ["catalog.write", "settings.read"] },
    ],
  },
  {
    title: "Student App",
    items: [
      { to: "/student", label: "Dashboard", icon: LayoutDashboard },
      { to: "/student/study", label: "Study Hub", icon: GraduationCap },
      { to: "/student/premium", label: "Premium Preview", icon: Sparkles },
    ],
  },
  {
    title: "System",
    items: [
      { to: "/admin/logs", label: "Logs", icon: ScrollText, feature: "logs.read" },
      { to: "/admin/settings", label: "Settings", icon: Settings, feature: "settings.read" },
      { to: "/admin/broadcast", label: "Broadcast", icon: Megaphone, feature: "users.write" },
      { to: "/admin/devices", label: "Devices & sessions", icon: Shield },
      { to: "/admin/admins", label: "Admins", icon: UserCog, feature: "admins.manage" },
    ],
  },
];

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, tokens } = useAuth();
  const segment = location.pathname.split("/").pop() || "overview";
  const breadcrumb = segment === "admin" ? "overview" : segment.replace(/-/g, " ");
  const isSuper = !!tokens?.isSuperAdmin;

  const [profile, setProfile] = useState<{ fullName: string; phone: string; avatar: string } | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!tokens?.accessToken) return;
    api.profile(tokens.accessToken)
      .then((p) => setProfile({ fullName: p.fullName, phone: p.phone, avatar: p.avatar }))
      .catch(() => setProfile({ fullName: "Admin", phone: "", avatar: "AD" }));
  }, [tokens?.accessToken]);

  const visibleSections = useMemo(() => (
    navSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.features?.length) return canAccessAny(tokens, item.features);
          if (item.feature) return canAccess(tokens, item.feature);
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0)
  ), [tokens]);

  const displayName = profile?.fullName ?? "Admin";
  const avatar = profile?.avatar ?? "AD";
  const phone = profile?.phone ?? "";

  const sidebarContent = (
    <div className="h-full bg-card border border-border flex flex-col overflow-hidden rounded-rail">
      <div className="px-4 py-3.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img
            src="/logo/soorgaa_benchmark1.png"
            alt="Soorgaa Logo"
            className="w-8 h-8 object-contain shrink-0 dark:hidden"
          />
          <img
            src="/logo/soorgaa White and Orange bench mark.png"
            alt="Soorgaa Logo"
            className="w-8 h-8 object-contain shrink-0 hidden dark:block"
          />
          <div>
            <p className="font-display font-bold text-base leading-none text-foreground">Soorgaa</p>
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 font-semibold uppercase tracking-wider">
              <Shield size={10} className="text-primary" /> {isSuper ? "Super Admin" : "Admin"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsMobileMenuOpen(false)}
          className="lg:hidden p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
          aria-label="Close admin navigation"
        >
          <X size={18} />
        </button>
      </div>

      <nav className="no-scrollbar min-h-0 flex-1 px-3 py-3 space-y-4 overflow-y-auto">
        {visibleSections.map((section) => (
          <div key={section.title}>
            <p className="px-2 mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {section.title}
            </p>
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `flex h-11 items-center gap-3 px-3 rounded-key text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-accent-soft text-primary"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`
                  }
                >
                  <item.icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsProfileModalOpen(true)}
            className="flex items-center gap-2.5 flex-1 min-w-0 text-left p-1 -m-1 rounded-md hover:bg-secondary transition group cursor-pointer"
            title="Manage profile & change password"
          >
            <div className="w-8 h-8 rounded-md bg-primary/10 text-primary border border-primary/20 flex items-center justify-center shrink-0 group-hover:border-primary/50">
              <span className="text-xs font-bold">{avatar}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate group-hover:text-primary transition-colors">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">
                {phone || (isSuper ? "Super admin" : "Administrator")}
              </p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setIsProfileModalOpen(true)}
            className="text-muted-foreground hover:text-primary p-2 rounded-md transition hover:bg-secondary cursor-pointer"
            title="Profile & security settings"
            aria-label="Profile & security settings"
          >
            <KeyRound size={16} />
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.adminLogout(getDeviceId(), undefined, tokens?.accessToken, tokens?.refreshToken);
              } catch {
                // ignore
              } finally {
                clearAdminDeviceToken(); // legacy localStorage token; trust now lives in an httpOnly cookie
                logout(true);
                navigate("/auth/admin");
              }
            }}
            className="text-muted-foreground hover:text-destructive p-2 rounded-md transition hover:bg-destructive/10 cursor-pointer"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-admin-ui className="min-h-svh flex bg-background text-foreground">
      {/* Desktop Sidebar */}
      <aside className="w-56 shrink-0 fixed inset-y-0 left-0 z-30 p-2.5 hidden lg:block">
        {sidebarContent}
      </aside>

      {/* Mobile Drawer Backdrop */}
      <Backdrop
        isOpen={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen(false)}
        className="lg:hidden"
        zIndex="z-40"
      />

      {/* Mobile Drawer Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 p-2.5 transition-transform duration-200 lg:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </div>

      {/* Main Admin Content */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-svh w-full min-w-0 lg:p-3">
        <div className="flex-1 flex flex-col min-w-0 bg-background lg:rounded-container lg:border lg:border-border">
        <header className="glass-overlay h-14 shrink-0 px-4 sm:px-6 flex items-center justify-between sticky top-0 z-20 border-b border-border lg:rounded-t-container">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-muted text-foreground cursor-pointer -ml-1"
              aria-label="Open admin navigation menu"
            >
              <Menu size={18} />
            </button>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="hidden sm:inline">Admin</span>
              <ChevronRight size={12} className="hidden sm:inline" />
              <span className="text-foreground font-semibold capitalize truncate">{breadcrumb}</span>
              <span
                className={`ml-2 px-2 py-0.5 rounded-xs text-xs font-semibold uppercase tracking-wider border ${
                  isSuper
                    ? "bg-primary/10 text-primary border-primary/20"
                    : "bg-secondary text-muted-foreground border-border"
                }`}
              >
                {isSuper ? "Super" : "Standard"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setIsProfileModalOpen(true)}
              className="w-8 h-8 rounded-md bg-primary/10 text-primary border border-primary/20 flex items-center justify-center text-xs font-bold hover:border-primary/50 transition cursor-pointer"
              title="Manage profile & password"
              aria-label="Manage profile & password"
            >
              {avatar}
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 py-6 pb-8 w-full max-w-7xl mx-auto">
          <AdminErrorBoundary
            resetKey={location.pathname}
            user={phone ? `${displayName} (${phone})` : displayName}
            token={tokens?.accessToken ?? null}
          >
            <Outlet />
          </AdminErrorBoundary>
        </main>
        </div>
      </div>

      <AdminProfileModal
        open={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onProfileUpdated={(updated) => {
          setProfile((prev) => (prev ? { ...prev, fullName: updated.fullName } : null));
        }}
      />
    </div>
  );
}
